import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Shield, Heart, Briefcase } from 'lucide-react';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import ContactForm from '../components/ContactForm/ContactForm';
import LegalNotice from '../components/LegalNotice/LegalNotice';
import { trackGoal, trackEvent, GOALS } from '../utils/yandexMetrika';
import { setPageMetadata } from '../utils/seo';
import './ServiceDetails.css';

const ServiceDetails = () => {
  const { serviceId } = useParams();
  const [showForm, setShowForm] = useState(false);

  const servicesData = {
    rvp: {
      title: 'Разрешение на временное проживание (РВП)',
      subtitle: 'Первый шаг к легальному проживанию в России',
      description: 'РВП позволяет законно жить и работать в России в течение трех лет. Это обязательный этап перед получением вида на жительство.',
      benefits: [
        {
          icon: <Shield size={24} />,
          title: 'Полное сопровождение',
          text: 'От первой консультации до получения штампа в паспорт'
        },
        {
          icon: <Heart size={24} />,
          title: 'Индивидуальный подход',
          text: 'Учитываем вашу ситуацию и подбираем оптимальное основание'
        },
        {
          icon: <Briefcase size={24} />,
          title: 'Работаем до результата',
          text: 'Сопровождаем вас на всех этапах процедуры'
        }
      ],
      process: [
        {
          title: 'Консультация',
          description: 'Анализируем вашу ситуацию и определяем основания для получения РВП'
        },
        {
          title: 'Подготовка документов',
          description: 'Собираем и правильно оформляем весь пакет документов'
        },
        {
          title: 'Медкомиссия и экзамены',
          description: 'Оказываем помощь для прохождения медкомиссии и экзамена по русскому языку, основ законодательства и истории России'
        },
        {
          title: 'Подача заявления',
          description: 'Обеспечиваем полную информационную и организационную поддержку при подаче документов в МВД — мы всегда на связи, поможем разобраться в процедуре, подготовить пакет документов и учесть все нюансы'
        },
        {
          title: 'Контроль процесса',
          description: 'Отслеживаем статус рассмотрения и информируем вас'
        },
        {
          title: 'Получение РВП',
          description: 'Помогаем получить решение и проставить штамп'
        }
      ],
      important: [
        'РВП выдается на 3 года без права продления',
        'Необходимо ежегодно подтверждать проживание',
        'Можно работать только в регионе получения РВП',
        'Через год после РВП можно подавать на ВНЖ',
        'Стоимость услуг определяется индивидуально на бесплатной консультации — зависит от состава документов, оснований и срочности. Государственные пошлины оплачиваются отдельно.'
      ]
    },
    vnzh: {
      title: 'Вид на жительство (ВНЖ)',
      subtitle: 'Постоянное проживание в России',
      description: 'ВНЖ дает право постоянно проживать в России, свободно выезжать и въезжать, работать в любом регионе страны.',
      benefits: [
        {
          icon: <Shield size={24} />,
          title: 'Экспертная помощь',
          text: 'Знаем все тонкости оформления ВНЖ'
        },
        {
          icon: <Heart size={24} />,
          title: 'Поддержка на всех этапах',
          text: 'От подготовки документов до получения книжки ВНЖ'
        },
        {
          icon: <Briefcase size={24} />,
          title: 'Решение сложных случаев',
          text: 'Работаем с нестандартными ситуациями'
        }
      ],
      process: [
        {
          title: 'Проверка готовности',
          description: 'Убеждаемся, что вы соответствуете требованиям для ВНЖ'
        },
        {
          title: 'Сбор документов',
          description: 'Готовим полный пакет документов согласно вашему основанию'
        },
        {
          title: 'Подтверждение дохода',
          description: 'Помогаем оформить и подготовить необходимые справки о доходах. Консультируем, какие документы подойдут для подтверждения дохода'
        },
        {
          title: 'Медкомиссия и экзамены',
          description: 'Оказываем помощь для прохождения медкомиссии и экзамена по русскому языку, основ законодательства и истории России'
        },
        {
          title: 'Подача заявления',
          description: 'Правильно заполняем и подаем документы'
        },
        {
          title: 'Получение ВНЖ',
          description: 'Сопровождаем до получения книжки ВНЖ'
        }
      ],
      important: [
        'С 2023 года вид на жительство (ВНЖ) в России выдается бессрочно, продление не требуется',
        'ВНЖ позволяет работать в любом регионе РФ без патента или разрешения',
        'Необходимо ежегодно подтверждать проживание в России — не менее 180 дней в году',
        'Право на получение гражданства РФ можно оформить уже через 5 лет после получения ВНЖ',
        'При наличии оснований, упрощающих получение гражданства (например, наличие ребенка-гражданина РФ, трудовой стаж, высшее образование в России и др.) — можно подать документы до истечения 5 лет',
        'Стоимость услуг определяется индивидуально на бесплатной консультации — зависит от состава документов, оснований и срочности. Государственные пошлины оплачиваются отдельно.'
      ]
    },
    citizenship: {
      title: 'Гражданство РФ',
      subtitle: 'Станьте гражданином России',
      description: 'Получение гражданства РФ — это завершающий этап легализации в стране. Став гражданином, вы получаете полный пакет прав, включая возможность оформить российский паспорт и пользоваться всеми преимуществами проживания в России.',
      benefits: [
        {
          icon: <Shield size={24} />,
          title: 'Полное сопровождение документов',
          text: 'Проведем через все этапы получения гражданства'
        },
        {
          icon: <Heart size={24} />,
          title: 'Подготовка к экзаменам',
          text: 'Поможем сдать тесты по языку, истории и праву'
        },
        {
          icon: <Briefcase size={24} />,
          title: 'Работа с отказами',
          text: 'Поможем даже если ранее был отказ'
        }
      ],
      process: [
        {
          title: 'Определение оснований',
          description: 'Выбираем общий или упрощенный порядок получения'
        },
        {
          title: 'Проверка документов',
          description: 'Анализируем ваши документы и готовим недостающие'
        },
        {
          title: 'Подготовка к тестам',
          description: 'Готовим к экзаменам по русскому языку, истории и законам РФ'
        },
        {
          title: 'Оформление заявления',
          description: 'Правильно заполняем все формы и заявления'
        },
        {
          title: 'Подача документов',
          description: 'Сопровождаем при подаче в отдел по гражданству'
        },
        {
          title: 'Присяга и паспорт',
          description: 'Помогаем с присягой и получением паспорта РФ'
        }
      ],
      important: [
        'Необходимо отказаться от предыдущего гражданства',
        'Требуется знание русского языка, истории и законов',
        'Процесс занимает от 3 месяцев до года',
        'После присяги вы становитесь гражданином РФ',
        'Стоимость услуг определяется индивидуально на бесплатной консультации — зависит от состава документов, оснований и срочности. Государственные пошлины оплачиваются отдельно.'
      ]
    },
    translation: {
      title: 'Бюро Переводов',
      subtitle: 'Профессиональный перевод документов',
      description: 'Переводим и нотариально заверяем любые документы для миграционных служб. Работаем быстро и качественно.',
      benefits: [
        {
          icon: <Shield size={24} />,
          title: 'Официальные переводы',
          text: 'Все переводы с нотариальным заверением'
        },
        {
          icon: <Heart size={24} />,
          title: 'Срочный перевод',
          text: 'Можем перевести документы за один день'
        },
        {
          icon: <Briefcase size={24} />,
          title: 'Принимаются в госорганах',
          text: 'Переводы оформляются по требованиям, предъявляемым к нотариальному переводу'
        }
      ],
      services: [
        {
          category: 'Личные документы',
          items: ['Паспорта', 'Свидетельства о рождении', 'Свидетельства о браке/разводе']
        },
        {
          category: 'Документы об образовании',
          items: ['Дипломы', 'Аттестаты', 'Академические справки']
        },
        {
          category: 'Справки',
          items: ['О несудимости', 'Медицинские', 'С места работы']
        },
        {
          category: 'Другие документы',
          items: ['Доверенности', 'Договоры', 'Любые документы для МВД']
        }
      ],
      important: [
        'Работаем со всеми языками мира',
        'Нотариальное заверение включено в стоимость',
        'Срок перевода от 1 до 3 дней',
        'Выдача переводов только в офисе Подольска',
        'Стоимость зависит от языка, объёма и срочности — точную цену сообщим на бесплатной консультации.'
      ]
    }
  };

  const service = servicesData[serviceId] || servicesData.rvp;

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMetadata({
      title: `${service.title} в Подольске — Мигрант PRO`,
      description: `${service.description} Бесплатная консультация и сопровождение в Подольске и Московской области.`,
      path: `/service/${serviceId}`,
    });
    // Отслеживаем просмотр услуги
    trackGoal(GOALS.SERVICE_VIEW, { 
      service_id: serviceId,
      service_name: service.title 
    });
    trackEvent('Service', 'View', service.title);
  }, [serviceId, service.title, service.description]);

  const handleConsultationClick = () => {
    setShowForm(true);
    // Отслеживаем клик по кнопке консультации
    trackEvent('Service', 'Consultation Click', service.title);
    
    setTimeout(() => {
      const formElement = document.getElementById('service-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  return (
    <>
      <Header />
      <div className="service-details">
        <div className="service-hero">
          <div className="container">
            <Link to="/" className="back-link">
              <ArrowLeft size={20} />
              Вернуться на главную
            </Link>
            
            <h1>{service.title}</h1>
            <p className="service-subtitle">{service.subtitle}</p>
            <p className="service-description">{service.description}</p>
          </div>
        </div>

        <div className="container">
          {/* Преимущества */}
          <section className="benefits-section">
            <h2>Что мы предлагаем</h2>
            <div className="benefits-grid">
              {service.benefits.map((benefit, index) => (
                <div key={index} className="benefit-card">
                  <div className="benefit-icon">{benefit.icon}</div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Процесс для миграционных услуг */}
          {service.process && (
            <section className="process-section">
              <h2>Этапы работы</h2>
              <div className="process-grid">
                {service.process.map((step, index) => (
                  <div key={index} className="process-card">
                    <div className="process-number">{index + 1}</div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Услуги переводов */}
          {service.services && (
            <section className="translation-services">
              <h2>Что мы переводим</h2>
              <div className="translation-grid">
                {service.services.map((category, index) => (
                  <div key={index} className="translation-category">
                    <h3>{category.category}</h3>
                    <ul>
                      {category.items.map((item, i) => (
                        <li key={i}>
                          <CheckCircle size={16} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Важная информация */}
          <section className="important-section">
            <h2>Важно знать</h2>
            <div className="important-grid">
              {service.important.map((info, index) => (
                <div key={index} className="important-item">
                  <div className="important-marker"></div>
                  <p>{info}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA секция с плавной анимацией */}
          <section className="cta-section">
            <div className={`cta-wrapper ${showForm ? 'show-form' : ''}`}>
              <div className="cta-content">
                <h2>Остались вопросы?</h2>
                <p>Получите бесплатную консультацию по услуге "{service.title}"</p>
                <button 
                  className="cta-button"
                  onClick={handleConsultationClick}
                >
                  Получить консультацию
                </button>
                <LegalNotice variant="consultation" className="cta-legal-notice" />
              </div>
              
              <div id="service-form" className="service-form-container">
                <ContactForm />
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServiceDetails;
